import React, { useState } from 'react';
import Icon from '@salesforce/design-system-react/components/icon';
import Button from '@salesforce/design-system-react/components/button';

// ---------------------------------------------------------------------------
// Style constants
// ---------------------------------------------------------------------------
const COLORS = {
  brand: '#0176d3',
  success: '#2e844a',
  warning: '#fe9339',
  error: '#c23934',
  border: '#dddbda',
  bg: '#f3f3f3',
  textPrimary: '#080707',
  textSecondary: '#706e6b',
  white: '#ffffff',
  aiMessageBg: '#f4f6f9',
  userMessageBg: '#0176d3',
  userMessageText: '#ffffff',
};

const Badge = ({ label, color, bgColor }) => (
  <span style={{
    display: 'inline-block',
    backgroundColor: bgColor,
    color: color,
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600',
    lineHeight: '16px',
    marginRight: '8px',
  }}>
    {label}
  </span>
);

const DataPoint = ({ icon, label, value, color }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: COLORS.white,
    borderRadius: '4px',
    border: `1px solid ${COLORS.border}`,
  }}>
    <Icon category="utility" name={icon} size="x-small" style={{ fill: color || COLORS.brand }} />
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{ fontSize: '14px', color: COLORS.textPrimary, fontWeight: '600' }}>
        {value}
      </div>
    </div>
  </div>
);

const ChatMessage = ({ type, content, timestamp, showContext }) => {
  const isUser = type === 'user';

  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: '20px',
    }}>
      <div style={{
        maxWidth: '75%',
        display: 'flex',
        flexDirection: isUser ? 'row-reverse' : 'row',
        gap: '12px',
        alignItems: 'flex-start',
      }}>
        {/* Avatar */}
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          backgroundColor: isUser ? COLORS.brand : '#9050e9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon
            category="utility"
            name={isUser ? 'user' : 'einstein'}
            size="x-small"
            style={{ fill: COLORS.white }}
          />
        </div>

        {/* Message Content */}
        <div style={{ flex: 1 }}>
          <div style={{
            backgroundColor: isUser ? COLORS.userMessageBg : COLORS.aiMessageBg,
            color: isUser ? COLORS.userMessageText : COLORS.textPrimary,
            padding: '14px 16px',
            borderRadius: '12px',
            fontSize: '14px',
            lineHeight: '1.5',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}>
            {content}
          </div>

          {/* Context Cards */}
          {showContext && !isUser && (
            <div style={{
              marginTop: '12px',
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '8px',
            }}>
              {showContext.map((ctx, idx) => (
                <DataPoint key={idx} {...ctx} />
              ))}
            </div>
          )}

          {/* Timestamp */}
          <div style={{
            fontSize: '11px',
            color: COLORS.textSecondary,
            marginTop: '6px',
            textAlign: isUser ? 'right' : 'left',
          }}>
            {timestamp}
          </div>
        </div>
      </div>
    </div>
  );
};

const NPSPAIAssistant = () => {
  const [inputValue, setInputValue] = useState('');

  const conversation = [
    {
      type: 'ai',
      content: "Hello! I'm your NPSP AI Assistant. I can help you with donor insights, meeting preparation, donation history, and more. How can I assist you today?",
      timestamp: '10:23 AM',
    },
    {
      type: 'user',
      content: "I have a meeting with Robert Chen tomorrow. Can you help me prepare?",
      timestamp: '10:24 AM',
    },
    {
      type: 'ai',
      content: "Of course! Robert Chen is one of your major donors. Let me pull up his key information for your meeting preparation.",
      timestamp: '10:24 AM',
      showContext: [
        { icon: 'moneybag', label: 'Lifetime Giving', value: '$285,000', color: COLORS.success },
        { icon: 'date_time', label: 'Last Donation', value: 'Jan 15, 2025', color: COLORS.brand },
        { icon: 'trending', label: 'Giving Trend', value: '+15% YoY', color: COLORS.success },
        { icon: 'heart', label: 'Donor Level', value: 'Platinum', color: '#9050e9' },
      ],
    },
    {
      type: 'user',
      content: "What did we talk about in our last meeting?",
      timestamp: '10:25 AM',
    },
    {
      type: 'ai',
      content: "Based on your last meeting on December 8, 2024, you discussed:\n\n• His interest in the new STEM Education Initiative\n• Potential matching gift opportunity for Q1 2025\n• His daughter's upcoming college graduation\n• Request for impact report on previous scholarship donations\n\nHe mentioned wanting to increase his involvement with the scholarship program and potentially establishing a named fund.",
      timestamp: '10:25 AM',
    },
    {
      type: 'user',
      content: "Has he made any donations since that meeting?",
      timestamp: '10:26 AM',
    },
    {
      type: 'ai',
      content: "Yes! Robert made a significant donation on January 15, 2025:",
      timestamp: '10:26 AM',
      showContext: [
        { icon: 'currency', label: 'Amount', value: '$50,000', color: COLORS.success },
        { icon: 'education', label: 'Designation', value: 'STEM Initiative', color: COLORS.brand },
        { icon: 'check', label: 'Status', value: 'Posted', color: COLORS.success },
        { icon: 'date_time', label: 'Payment Date', value: 'Jan 15, 2025', color: COLORS.textSecondary },
      ],
    },
    {
      type: 'user',
      content: "Perfect! Any key talking points I should mention?",
      timestamp: '10:27 AM',
    },
    {
      type: 'ai',
      content: "Great question! Here are the key talking points for your meeting:\n\n1. **Thank Him:** Acknowledge his recent $50,000 STEM Initiative gift and its impact on 25 students this semester\n\n2. **Impact Update:** Share that the scholarship program he supports has a 94% graduation rate, up from 87% last year\n\n3. **Matching Gift:** Follow up on his December interest in Q1 matching gift - we've identified 15 eligible donors who could participate\n\n4. **Named Fund:** He expressed interest in establishing the 'Chen Family Scholarship Fund' - present the proposal and naming opportunities\n\n5. **Personal Touch:** Ask about his daughter's graduation plans (she's graduating from Stanford in May)\n\n6. **Future Engagement:** Invite him to the STEM Showcase Event on March 15th where students present their projects",
      timestamp: '10:28 AM',
    },
    {
      type: 'user',
      content: "This is incredibly helpful! What's his preferred communication style?",
      timestamp: '10:29 AM',
    },
    {
      type: 'ai',
      content: "Based on interaction history and engagement patterns:\n\n• **Prefers:** In-person meetings (92% attendance rate) and detailed email follow-ups\n• **Communication Frequency:** Quarterly touchpoints, with impact reports before major asks\n• **Best Days:** Tuesday-Thursday mornings\n• **Responds Well To:** Data-driven presentations, student testimonials, and long-term vision discussions\n• **Avoids:** Phone calls during business hours, generic appeals\n\nHe appreciates structured agendas and tends to make decisions during meetings rather than following up later. Bring the named fund proposal materials!",
      timestamp: '10:29 AM',
    },
  ];

  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: COLORS.bg,
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: COLORS.white,
        borderBottom: `1px solid ${COLORS.border}`,
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #9050e9 0%, #6a3bb3 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Icon category="utility" name="einstein" size="small" style={{ fill: COLORS.white }} />
          </div>
          <div>
            <h1 style={{
              fontSize: '18px',
              fontWeight: '700',
              margin: 0,
              color: COLORS.textPrimary,
            }}>
              NPSP AI Assistant
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
              <Badge label="EINSTEIN" color="#6a3bb3" bgColor="#f0e5ff" />
              <span style={{ fontSize: '13px', color: COLORS.textSecondary }}>
                Powered by Salesforce AI
              </span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button label="Export Chat" variant="neutral" />
          <Button label="New Conversation" />
        </div>
      </div>

      {/* Context Banner */}
      <div style={{
        backgroundColor: '#f0e5ff',
        borderBottom: `1px solid #e5d5ff`,
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <Icon category="utility" name="info" size="x-small" style={{ fill: '#6a3bb3' }} />
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: '13px', color: '#3e3e3c', fontWeight: '600' }}>
            Active Context:
          </span>
          <span style={{ fontSize: '13px', color: '#706e6b', marginLeft: '8px' }}>
            Robert Chen (Contact) • Major Donor • Meeting Prep Mode
          </span>
        </div>
      </div>

      {/* Chat Messages */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '24px',
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {/* Date Divider */}
          <div style={{
            textAlign: 'center',
            marginBottom: '24px',
          }}>
            <span style={{
              display: 'inline-block',
              backgroundColor: COLORS.white,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '16px',
              padding: '6px 16px',
              fontSize: '12px',
              color: COLORS.textSecondary,
              fontWeight: '600',
            }}>
              Today, February 12, 2025
            </span>
          </div>

          {/* Messages */}
          {conversation.map((msg, idx) => (
            <ChatMessage key={idx} {...msg} />
          ))}

          {/* Typing Indicator (optional - shown when AI is "thinking") */}
          {/* <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: '#9050e9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Icon category="utility" name="einstein" size="x-small" style={{ fill: COLORS.white }} />
            </div>
            <div style={{
              backgroundColor: COLORS.aiMessageBg,
              padding: '14px 16px',
              borderRadius: '12px',
              display: 'flex',
              gap: '4px',
            }}>
              <span style={{ fontSize: '20px', animation: 'pulse 1.4s infinite' }}>•</span>
              <span style={{ fontSize: '20px', animation: 'pulse 1.4s infinite 0.2s' }}>•</span>
              <span style={{ fontSize: '20px', animation: 'pulse 1.4s infinite 0.4s' }}>•</span>
            </div>
          </div> */}
        </div>
      </div>

      {/* Input Area */}
      <div style={{
        backgroundColor: COLORS.white,
        borderTop: `1px solid ${COLORS.border}`,
        padding: '16px 24px',
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {/* Suggested Actions */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '12px',
            flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: '12px', color: COLORS.textSecondary, marginRight: '4px' }}>
              Quick Actions:
            </span>
            {['Show all donations', 'Recent activities', 'Engagement score', 'Household info'].map((action) => (
              <button
                key={action}
                style={{
                  fontSize: '12px',
                  color: COLORS.brand,
                  backgroundColor: COLORS.white,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '12px',
                  padding: '4px 12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f4f6f9';
                  e.target.style.borderColor = COLORS.brand;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = COLORS.white;
                  e.target.style.borderColor = COLORS.border;
                }}
              >
                {action}
              </button>
            ))}
          </div>

          {/* Input Field */}
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-end',
          }}>
            <div style={{ flex: 1 }}>
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask me about donors, donations, campaigns, or upcoming meetings..."
                style={{
                  width: '100%',
                  minHeight: '52px',
                  maxHeight: '120px',
                  padding: '12px 16px',
                  fontSize: '14px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '8px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  outline: 'none',
                }}
                onFocus={(e) => e.target.style.borderColor = COLORS.brand}
                onBlur={(e) => e.target.style.borderColor = COLORS.border}
              />
            </div>
            <Button
              label="Send"
              variant="brand"
              iconCategory="utility"
              iconName="send"
              iconPosition="right"
              iconSize="x-small"
              disabled={!inputValue.trim()}
              style={{ marginBottom: '0' }}
            />
          </div>

          {/* Footer Info */}
          <div style={{
            marginTop: '12px',
            fontSize: '11px',
            color: COLORS.textSecondary,
            textAlign: 'center',
          }}>
            <Icon category="utility" name="info" size="xx-small" style={{ fill: COLORS.textSecondary, marginRight: '4px' }} />
            AI responses are generated based on your Salesforce data. Always verify critical information.
          </div>
        </div>
      </div>
    </div>
  );
};

export default NPSPAIAssistant;
